import type { BadgeProps } from '../../../../core'
import type { components } from '@joyful/openapi/types.gen'
import type { EnumValueMeta } from '@joyful/utils'
import { createEnumSchema } from '@joyful/utils'
import type { LucideIcon } from 'lucide-react'
import {
  CalendarRangeIcon,
  CircleDashedIcon,
  CircleIcon,
  SendHorizontalIcon,
  SkipForwardIcon,
  TrendingUpIcon,
} from 'lucide-react'

type KpiCsvColumnKey = components['schemas']['KpiCsvColumnKey']
type KpiEmailState = components['schemas']['KpiEmailState']
type KpiEmailFrequency = components['schemas']['KpiEmailFrequency']
type KpiMetricsMode = components['schemas']['KpiMetricsMode']

export const KpiEmailStateSchema = createEnumSchema<
  KpiEmailState,
  EnumValueMeta & { icon: LucideIcon; variant: BadgeProps['variant'] }
>([
  {
    icon: CircleDashedIcon,
    title: 'Draft',
    value: 'draft',
    variant: 'warning',
  },
  {
    icon: CircleIcon,
    title: 'Ready',
    value: 'ready',
    variant: 'info',
  },
  {
    icon: SendHorizontalIcon,
    title: 'Sent',
    value: 'sent',
    variant: 'success',
  },
  {
    icon: SkipForwardIcon,
    title: 'Skipped',
    value: 'skipped',
    variant: 'neutral',
  },
])

interface KpiCsvColumnKeyMeta extends EnumValueMeta {
  isIncludedByDefault?: boolean
}

export const KpiCsvColumnKeySchema = createEnumSchema<
  KpiCsvColumnKey,
  KpiCsvColumnKeyMeta
>([
  {
    title: 'Platform URL',
    value: 'url',
  },
  {
    isIncludedByDefault: true,
    title: 'Patient name',
    value: 'patient_name',
  },
  {
    isIncludedByDefault: true,
    title: 'Service date',
    value: 'start_date',
  },
  {
    isIncludedByDefault: true,
    title: 'Claim status',
    value: 'claim_status',
  },
  {
    isIncludedByDefault: true,
    title: 'Charge amount',
    value: 'charge_amount',
  },
  {
    title: 'Recovered amount',
    value: 'recovered_amount',
  },
  {
    isIncludedByDefault: true,
    title: 'All biller notes',
    value: 'biller_notes',
  },
])

export const DefaultKpiCsvColumnKeySchema = createEnumSchema(
  KpiCsvColumnKeySchema.definitions.filter((definition) => {
    return definition.isIncludedByDefault === true
  }),
)

export const KpiEmailFrequencySchema = createEnumSchema<KpiEmailFrequency>([
  {
    description: 'Emails are sent every week.',
    title: 'Weekly',
    value: 'weekly',
  },
  {
    description: 'Emails are sent every two weeks.',
    title: 'Biweekly',
    value: 'biweekly',
  },
])

export const KpiMetricsModeSchema = createEnumSchema<
  KpiMetricsMode,
  EnumValueMeta & { icon: LucideIcon }
>([
  {
    description: 'Metrics are calculated for the specific email period only.',
    icon: CalendarRangeIcon,
    title: 'Email period',
    value: 'period',
  },
  {
    description:
      'Metrics are calculated from the account go-live date to the period end.',
    icon: TrendingUpIcon,
    title: 'All time (cumulative)',
    value: 'cumulative',
  },
])
